<template>
    <div>
        <!-- <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert> -->
        <core-error-alerts :errors="errorMsgList"></core-error-alerts>
        
        <transition name="fade-fast" mode="out-in">
        
            <busy-spinner v-if="loading" key="loadingTrue" inner-class="my-4" text="Loading..."></busy-spinner>

            <div v-else key="loadingFalse">
                
                <p class="text-muted font-size-70 font-italic">
                    Submitted 
                    <span v-if="submittedByName">
                        by <span class="text-dark font-weight-500">{{ submittedByName }}</span>
                    </span>
                    on {{ formatDateTime(reply.submitted_on) }}
                </p>
                <div 
                    v-for="form in allForms"
                    :key="form.id"
                    class="mb-4"
                >
                    <div class="d-flex align-items-center justify-content-between pb-2 border-bottom border-primary">
                        <h5 class="text-primary-darker mb-0">{{ form.label }}</h5>
                        <div class="d-flex flex-wrap align-items-center" style="gap: 0.5rem;">
                            <b-dropdown :disabled="isDownloadingSummary" size="sm" variant="gray-primary">
                                <template #button-content>
                                    <b-icon v-if="!isDownloadingSummary" icon="download" class="mr-1"></b-icon>
                                    <span class="">{{ isDownloadingSummary ? 'Downloading...' : 'Download' }}</span>
                                </template>
                                <b-dropdown-item :disabled="isDownloadingSummary || !form.id" @click="downloadSummaryFile('xlsx')">
                                    <b-icon icon="file-earmark-spreadsheet" class="mr-1"></b-icon>
                                    Download Excel
                                </b-dropdown-item>
                                <b-dropdown-item :disabled="isDownloadingSummary" @click="downloadSummaryFile('pdf')">
                                    <b-icon icon="file-earmark-pdf" class="mr-1"></b-icon>
                                    Download PDF
                                </b-dropdown-item>
                                <b-dropdown-item :disabled="isDownloadingSummary" @click="downloadSummaryFile('txt')">
                                    <b-icon icon="file-earmark-text" class="mr-1"></b-icon>
                                    Download Text
                                </b-dropdown-item>
                            </b-dropdown>
                            <!-- <b-button size="sm" variant="gray-primary" v-b-tooltip.hover title="Download Text Summary (Experimental)" @click="downloadSummaryFile('txt')">
                                <b-icon icon="file-earmark-text" class=""></b-icon>
                            </b-button>
                            <b-button size="sm" variant="gray-primary" v-b-tooltip.hover title="Download PDF Summary (Experimental)" @click="downloadSummaryFile('pdf')">
                                <b-icon icon="file-earmark-pdf" class=""></b-icon>
                            </b-button> -->
                            <b-button size="sm" variant="gray-primary" v-b-tooltip.hover title="View Full Submission" @click="showDetailModal">
                                <b-icon icon="arrows-fullscreen" class="mr-1"></b-icon>
                                View
                            </b-button>
                        </div>
                        
                    </div>
                    <b-table
                        v-if="form.items.length"
                        :items="form.items"
                        :fields="formCols"
                        :tbody-tr-class="getRowClass"
                        small
                        class="smartform-reply-viewer-table no-top-border"
                    >
                    </b-table>
                </div>

                <!-- <pre>{{ replyData }}</pre> -->
            </div>
        
        </transition>

        <b-modal
            v-model="detailModalVisible"
            centered
            size="xxl"
            :title="form.label || 'Quote Template'"
            body-class="bg-gray-100"
            content-class="overflow-hidden"
            no-close-on-esc
            no-close-on-backdrop
            hide-footer
            hide-header
            scrollable
        >
            <!-- we force readOnly to be safe although not needed when reply is submitted -->
            <smart-form-formio
                :replyId="replyId"
                :readOnly="true"
                @request-close="hideDetailModal"
            ></smart-form-formio>
        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling } from '../../js/custom-vue-mixins';
import { FormatDateTimeLocal } from '../../js/filters-helpers';
import { formatSmartFormValue } from './smartforms-helpers';
import { sanitizeFilename } from '@/js/utils/stringUtils';
import { SmartFormCompareGridRow } from '../company_todos/company-todo-compare-grid-helpers';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';

export default {
    name: 'SmartFormReplyViewer',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        replyId: {
            type: String,
            required: true,
        }
    },
    data: function() {
        return {
            loading: true,  // set to true for this, so it always starts in loading mode
            form: {},  // SmartForm object - the form this is a reply to  (top-level, in case there are child forms)
            reply: {},  // SmartFormReply object - but NO SUBMISSION DATA - just 'metadata'
            company: {},  // company info for this reply -- just {id, name} for now
            fieldMap: {},  // get from form.field_list - mapping of fields by their ID
            liveFieldMap: {},
            submissionData: {},  // the submission data for the reply
            tableItems: [],  // we build this after API call -- this is what is shown in the actual table
            allForms: [],  // NEW Sept 5 2022 - array of forms, each with own flat list of items - so we can show child forms
            columns: [
                {
                    key: 'label',
                    label: 'Item',
                    thClass: 'min-width-200',  // using this to set a min width for the question column
                    tdClass: 'font-weight-bold',
                },
                {
                    key: 'value',
                    label: 'Response',
                    tdClass: 'text-show-breaks',
                }
            ],
            formCols: [
                {
                    key: 'label',
                    label: 'Item',
                    thClass: 'w-60',  // using this to set a min width for the question column
                    tdClass: 'font-weight-bold',
                },
                {
                    key: 'singleReplyValue',
                    label: 'Response',
                    thClass: 'w-40',
                    tdClass: 'text-show-breaks',
                    formatter: (value, key, item) => {
                        // use the valueStringFormatter(val) method from our class instance
                        const val = item.data_path ? get(this.submissionData, item.data_path, '') : '';
                        return item.valueStringFormatter(val);
                    },
                }
            ],
            // detail modal stuff
            detailModalVisible: false,
            isDownloadingSummary: false,  // to disable buttons while downloading summary file
        }
    },
    computed: {
        submittedByName() {
            return this.reply?.submitted_by?.full_name ?? '';
        },
        formName() {
            return this.form?.name ?? '';  // the name of the TOP-LEVEL form, in case there are multiple
        },
    },
    mounted: function() {
        this.getReply();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getReply() {
            this.loading = true;
            try {
                const { data } = await suppliosApi.smartFormReply.get_for_review(this.replyId);
                this.reply = Object.assign({}, data.reply);
                this.submissionData = Object.assign({}, data.submission);
                this.form = Object.assign({}, data.form);
                this.company = Object.assign({}, data.company);
                const fieldList = data.form.field_list || [];
                const liveFieldList = data.form.compare_grid_fields || [];
                this.liveFieldMap = Object.fromEntries(liveFieldList.map(fld => ([fld.id, fld])));
                this.fieldMap = Object.fromEntries(fieldList.map(fld => ([fld.id, fld])));
            } catch (err) {
                this.apiErrorHandler(err);
            }
            //this.buildTableItems();
            this.buildTableItemsCompareGrid();
            this.loading = false;
        },
        async downloadSummaryFile(format='txt') {
            this.isDownloadingSummary = true;
            try {
                let blob;
                let data;
                let rawFilename = `${this.formName}_submission_${this.replyId}.${format}`;
                if (format === 'txt') {
                    const resp = await suppliosApi.smartFormReply.get_markdown_text_file(this.replyId);
                    data = resp.data;
                    const filetype = resp.headers['content-type'] || 'text/plain';
                    blob = new Blob([data], { type: filetype });
                } else if (format === 'pdf') {
                    const resp = await suppliosApi.smartFormReply.get_markdown_pdf_file(this.replyId);
                    data = resp.data;
                    const filetype = resp.headers['content-type'] || 'application/pdf';
                    blob = new Blob([data], { type: filetype });
                } else if (format === 'xlsx') {
                    // includes any child forms, and internal-only fields
                    const resp = await suppliosApi.smartForm.download_reply(this.form.id, this.replyId);
                    data = resp.data;
                    const filetype = resp.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                    blob = new Blob([data], { type: filetype });
                    // use the filename from the backend if we have it - it includes vendor name and submitted date
                    const filenameMatch = (resp.headers['content-disposition'] || '').match(/filename="([^"]+)"/);
                    if (filenameMatch && filenameMatch[1]) {
                        rawFilename = filenameMatch[1];
                    }
                } else {
                    throw new Error(`Unsupported format: ${format}`);
                }
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                const cleanFilename = sanitizeFilename(rawFilename);
                link.download = cleanFilename;
                console.log('Initiating download of summary file with this link we constructed', link);
                document.body.appendChild(link);
                link.click();
                // Clean up and remove the link
                setTimeout(() => {
                    link.remove();  // or document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }, 100);
                // document.body.removeChild(link);
                // window.URL.revokeObjectURL(url);
            } catch (err) {
                this.apiErrorHandler(err, 'Failed to download file');
            }
            this.isDownloadingSummary = false;
        },
        buildTableItems() {
            
            // DEPRECATED - SEPT 6 2022 - REMOVE ONCE CONFIRMED OK
            
            // call this after you set API data to build and set this.tableItems
            // we are piggybacking off of the 'compare_grid' data for each field
            const tableItems = [];
            // Grouping? figure out what we should use for grouping of fields in the UI - we will set this as 'group_label' 
            const getGroup = (fld) => fld.parent ? this.fieldMap[fld.parent] : {};
            // FOR ANY ADDITIONAL CONFIG, YOU CAN ADD THINGS TO SmartForm itself and use that to figure out what to show, how to group, etc... 
            const fieldList = Object.values(this.fieldMap);
            fieldList.forEach(fld => {
                const exclude = fld.compare_grid === false; // if set specifically false
                const parentIsDataParent = fld.parent ? this.fieldMap[fld.parent].is_data_parent : false;  // DIRECT parent might be layout component, which would break this... but ok for now
                // check if we should exclude this row from the grid for any reason
                if (!fld.is_input || fld.is_data_parent || exclude || parentIsDataParent) {
                    console.log(`Not adding field ${fld.slug} to the review table`);
                    return  // bail out, do not add to table items
                }  // otherwise make a row for it in the table - pulling from fld and from submissionData
                const value_raw = this.submissionData[fld.slug] ?? '';
                const value = formatSmartFormValue(value_raw, fld);
                const group = getGroup(fld);
                let newItem = {
                    id: fld.id,
                    slug: fld.slug,
                    group_label: group.label ?? '',  // if we will group rows
                    group_position: group.position ?? 0,
                    position: fld.position,  // not always unique... but should be unique per group
                    label: fld.label,
                    value_raw: value_raw ?? '',
                    value: value,
                    // add classes? 
                    _rowVariant: fld.is_reply_input ? 'warning' : '',  // _rowVariant is special key for bootstrap-vue table component
                }
                tableItems.push(newItem);
            });
            // now sort by groups (by position of group), then position within groups
            this.tableItems = sortBy(tableItems, ['group_position', 'position']);
        },
        buildTableItemsCompareGrid() {
            // Sept 5 2022 - NEW way of builing rows for the grid, using the same way we build compare_grid rows
            // if you have child_forms, everything will be combined into one list of fields
            // you can split the different forms based on 'nested_path' in each one - if nested_path is only one item long, that's the top level
            // backend already figured out what to show or not
            //const items = [];
            const forms = {};  // will key by ID of the top-level form, then rest of the rows will be in flat list inside
            const addForm = (topLevelFormRow) => {
                // helper to stash a new top-level form in forms object... just a simple object constructor basically
                const newForm = {...topLevelFormRow};
                newForm['items'] = [];  // give it an empty array to hold all the rows... will be a flat list so its all nicely in a single table
                forms[newForm.id] = newForm;
            };
            const replies = [this.reply];
            Object.values(this.liveFieldMap).forEach(fld => {
                let newGridRow = new SmartFormCompareGridRow({smartform_field: fld, replies});
                // now, since we only have a single reply here (vs. many in the compare grid) we'll set an additional property with the FORMATTED value of the single reply
                // make sure it does not collide with existing stuff on the instance
                // it already has a value formatter method on it - just use that
                // newGridRow['singleReplyValue'] = newGridRow.valueStringFormatter(newGridRow.replies[this.reply.id]);
                // ACTUALLY, WE CAN'T DO IT EASILY... REPLY STRUCTURE IS DIFFERENT HERE AND REPLY GRID... DO IT IN TABLE COLDEF FORMATTER???
                if (newGridRow.nested_path.length === 1) {
                    // only top-level form rows will have nested_path length of 1
                    //console.log('adding new form row', newGridRow);
                    addForm(newGridRow);
                } else {
                    // otherwise, get the form this belongs to - either via form_id or by the first element in 'nested_path'
                    //console.log('adding child form row', newGridRow);
                    forms[newGridRow.form_id].items.push(newGridRow);
                }
            });
            this.allForms = Object.values(forms);
        },
        getRowClass(item, type) {
            //console.log('from getRowClass - here is item', item);
            // should return array, object, or string
            if (!item || type !== 'row') return;
            // items will have 'custom_classes' on them... just return that
            // will be things like 'internal', 'internal-only', 'external', 'calculated', 'smartform-input'
            // if it is not an input field, just an extra group row, add some other classes
            // also, we'll look at nested_path and indent things accordingly using 'level-0', etc... classes
            let classes = [];
            if (item.custom_classes && Array.isArray(item.custom_classes)) {
                //console.log('ADDING custom_classes');
                classes.push(...item.custom_classes);
            }
            /* if (item.is_reply_input) {
                console.log('ADDING is-reply-input CLASS');
                classes.push('is-reply-input');
                // just use 'external' class
            } */
            if (!item.data_path) {
                // if no data_path, then it must be a group row - that's our trigger
                //console.log('ADDING smartform-group-row class');
                classes.push('smartform-group-row');
            } else {
                const dpathLength = item.data_path.length;
                const levelClass = `level-${dpathLength - 1}`;
                //console.log('adding level class');
                classes.push(levelClass);
            }
            return classes;
        },
        formatDateTime: function(value) {
            return FormatDateTimeLocal(value);
        },
        // DETAIL MODAL
        showDetailModal() {
            // show modal, that handles fetching stuff on its own
            this.detailModalVisible = true;
        },
        hideDetailModal() {
            this.detailModalVisible = false;
        },
    }
}
</script>

<style lang="scss">

table.smartform-reply-viewer-table {

    $smartform-indent-spacing: 20px;

    tbody tr {

        &.smartform-group-row {
            background-color: var(--gray-200);
        }
        &.smartform-is-data-parent {
            background-color: var(--gray-100);
        }
        &.external {
            background-color: var(--warning-100);
        }
        &.emphasis {
            background-color: var(--primary-100);
        }

        &.level-0 td:first-of-type {
            padding-left: 0;
        }
        &.level-1 td:first-of-type {
            padding-left: calc(1 * $smartform-indent-spacing);
        }
        &.level-2 td:first-of-type {
            padding-left: calc(2 * $smartform-indent-spacing);
        }
        &.level-3 td:first-of-type {
            padding-left: calc(3 * $smartform-indent-spacing);
        }
    
    }
}

</style>